import { useState, useEffect, useMemo, useCallback } from 'react'
import { FiPlus, FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { API_URL } from '../../config/api'
import { Btn } from '../../ui'
import SolicitudAusenciaModal from './SolicitudAusenciaModal'

/*
 * MiCalendarioMes (Punto 4.B) — vista "Mi calendario" personal del empleado.
 * Pinta sus ausencias del mes (aprobadas sólidas, pendientes con borde
 * discontinuo), los festivos de su centro heredado (tinte rojo) y los días no
 * laborables de su calendario (gris). Festivos y días laborables vienen del
 * backend (GET /ausencias/mi-calendario), ya no hardcodeados.
 */

const TIPO_TONE = {
  vacaciones:           { color: '#10B981', label: 'Vacaciones' },
  dia_libre:            { color: '#2563EB', label: 'Día libre' },
  baja_medica:          { color: '#C13030', label: 'Baja médica' },
  baja_laboral:         { color: '#C13030', label: 'Baja laboral' },
  permiso_retribuido:   { color: '#7C3AED', label: 'Permiso retribuido' },
  asuntos_propios:      { color: '#0F8C66', label: 'Asuntos propios' },
  ausencia_justificada: { color: '#A86A06', label: 'Justificada' },
  otro:                 { color: '#64748B', label: 'Otro' },
}

const pad = (n) => String(n).padStart(2, '0')
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const parseISO = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d) }
const monthLabels = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']

// ISO del día (1=Lun … 7=Dom)
const isoDow = (d) => ((d.getDay() + 6) % 7) + 1

const monthGrid = (year, month) => {
  const first = new Date(year, month, 1)
  const startDow = (first.getDay() + 6) % 7 // 0=Lun
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells = []
  for (let i = startDow - 1; i >= 0; i--) cells.push({ date: new Date(year, month, -i), inMonth: false })
  for (let d = 1; d <= daysInMonth; d++) cells.push({ date: new Date(year, month, d), inMonth: true })
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1].date
    const next = new Date(last); next.setDate(last.getDate() + 1)
    cells.push({ date: next, inMonth: false })
  }
  return cells
}

export default function MiCalendarioMes({ onChange }) {
  const hoy = new Date()
  const [cursor, setCursor] = useState(new Date(hoy.getFullYear(), hoy.getMonth(), 1))
  const [data, setData] = useState({ dias_laborables: '1,2,3,4,5', festivos: [], ausencias: [], centro: null })
  const [loading, setLoading] = useState(false)
  const [modal, setModal] = useState(false)

  const mes = `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}`

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const r = await fetch(`${API_URL}/ausencias/mi-calendario?mes=${mes}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (r.ok) setData(await r.json())
    } catch {
      toast.error('Error al cargar el calendario')
    } finally {
      setLoading(false)
    }
  }, [mes])

  useEffect(() => { cargar() }, [cargar])

  const diasLab = useMemo(
    () => new Set(String(data.dias_laborables || '1,2,3,4,5').split(',').map(Number)),
    [data.dias_laborables]
  )
  const festivosMap = useMemo(() => {
    const m = {}; (data.festivos || []).forEach(f => { m[f.fecha] = f.nombre }); return m
  }, [data.festivos])
  const byDay = useMemo(() => {
    const m = {}
    ;(data.ausencias || []).forEach(a => {
      const from = parseISO(a.fecha_inicio), to = parseISO(a.fecha_fin)
      for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
        (m[ymd(d)] = m[ymd(d)] || []).push(a)
      }
    })
    return m
  }, [data.ausencias])

  const cells = monthGrid(cursor.getFullYear(), cursor.getMonth())
  const dows = ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM']

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))
  const goToday = () => setCursor(new Date(hoy.getFullYear(), hoy.getMonth(), 1))

  return (
    <div className="space-y-4">
      {/* Cabecera: mes + navegación + solicitar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <h3 className="font-display text-[18px] capitalize">{monthLabels[cursor.getMonth()]} {cursor.getFullYear()}</h3>
          {data.centro && <span className="text-[11px] text-[var(--ink-3)]">· {data.centro.nombre}</span>}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 border border-[var(--line)] rounded-pill px-1 h-9">
            <button onClick={goPrev} className="p-1.5 rounded-full hover:bg-[var(--bg-2)] text-[var(--ink-3)]"><FiChevronLeft size={14} /></button>
            <button onClick={goToday} className="px-3 h-7 rounded-full text-[12px] hover:bg-[var(--bg-2)] font-medium">Hoy</button>
            <button onClick={goNext} className="p-1.5 rounded-full hover:bg-[var(--bg-2)] text-[var(--ink-3)]"><FiChevronRight size={14} /></button>
          </div>
          <Btn variant="primary" size="sm" onClick={() => setModal(true)}><FiPlus size={14} /> Solicitar permiso</Btn>
        </div>
      </div>

      {/* Rejilla */}
      <div className="ui-card overflow-hidden">
        <div className="grid grid-cols-7 border-b border-[var(--line)]">
          {dows.map((d, i) => (
            <div key={d} className={`px-3 py-2.5 eyebrow ${i >= 5 ? 'text-[var(--ink-4)]' : ''}`}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((c, i) => {
            const k = ymd(c.date)
            const events = byDay[k] || []
            const isToday = ymd(hoy) === k
            const laborable = diasLab.has(isoDow(c.date))
            const fest = festivosMap[k]
            const dim = !c.inMonth
            const colIdx = (c.date.getDay() + 6) % 7
            return (
              <div
                key={i}
                className={`min-h-[96px] border-b border-[var(--line)] ${colIdx === 6 ? '' : 'border-r'} px-2 pt-1.5 pb-1.5
                  ${dim ? 'bg-[var(--bg-2)]/40' : ''}
                  ${!dim && !laborable ? 'bg-[var(--bg-2)]/60' : ''}
                  ${fest ? 'bg-[rgba(239,68,68,0.05)]' : ''}`}
                title={fest || (laborable ? '' : 'No laborable')}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`inline-grid place-items-center text-[11.5px] font-mono tnum
                    ${isToday ? 'w-6 h-6 rounded-full ia-grad-bg text-white font-semibold' : ''}
                    ${dim ? 'text-[var(--ink-4)]' : fest ? 'text-danger' : 'text-[var(--ink-2)]'}`}>
                    {c.date.getDate()}
                  </span>
                  {fest && (
                    <span className="text-[9px] font-mono uppercase tracking-wider text-danger truncate max-w-[64%]" title={fest}>{fest}</span>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  {events.map((ev, idx) => {
                    const t = TIPO_TONE[ev.tipo] || { color: '#64748B', label: ev.tipo }
                    const pendiente = ev.estado === 'pendiente'
                    return (
                      <div
                        key={`${ev.id}-${idx}`}
                        className="h-[18px] px-1.5 rounded-[5px] text-[10px] font-medium truncate flex items-center"
                        style={{
                          background: pendiente ? 'transparent' : `${t.color}1A`,
                          border: pendiente ? `1px dashed ${t.color}88` : `1px solid ${t.color}33`,
                          color: t.color
                        }}
                        title={`${t.label}${pendiente ? ' · pendiente' : ''}`}
                      >
                        {t.label}{pendiente ? ' ·' : ''}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Leyenda */}
      <div className="flex items-center gap-4 flex-wrap text-[11px] text-[var(--ink-3)]">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: '#10B98133', border: '1px solid #10B98155' }} /> Aprobada</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm border border-dashed" style={{ borderColor: 'var(--ink-3)' }} /> Pendiente</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[rgba(239,68,68,0.15)]" /> Festivo del centro</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[var(--bg-2)] border border-[var(--line)]" /> No laborable</span>
        {loading && <span className="text-[var(--ink-4)]">Cargando…</span>}
      </div>

      {modal && (
        <SolicitudAusenciaModal
          onClose={() => setModal(false)}
          onSuccess={() => { setModal(false); cargar(); if (onChange) onChange() }}
        />
      )}
    </div>
  )
}
